import { useLayoutEffect } from 'react'
import {
  applyTheme,
  setActiveForcedTheme,
  useThemeStore,
  type ThemeId,
} from '../lib/theme'
import { useFeatureFlag } from './useFeatureFlags'

/** Options for {@link useForcedTheme}. */
export interface ForcedThemeOptions {
  /**
   * Only force the theme while this is `true`. Defaults to `true`
   * (unconditional forcing, the original signature). Use it for dual-use public
   * routes that should force Daylight ONLY when signed out — pass
   * `{ when: isSignedIn === false }` so the loading state (`isSignedIn ===
   * undefined`) does NOT force, avoiding a flash of wrong forcing before auth
   * resolves.
   */
  when?: boolean
}

/**
 * Force `id` as the effective theme for the calling component's lifetime, then
 * restore the user's active theme on unmount.
 *
 * Gated on the `theme_picker` flag: while the flag is OFF this is a pure no-op
 * (zero visual change), so unauthenticated / marketing surfaces stay on
 * whatever the theme system already applied — dark (midnight) in production
 * today. Once the theme system is live the handoff rule is that signed-out
 * landing / pricing / auth surfaces render `light` (Daylight) ALWAYS; forcing
 * it here on the same flag is the batch-2 stand-in for that rule until the
 * launch flip makes `light` the pre-paint default.
 *
 * `options.when` (default `true`) makes the force conditional — dual-use routes
 * (`/services`, `/find-your-signs`) pass `{ when: isSignedIn === false }` so
 * they force Daylight only for signed-out visitors and honor the signed-in
 * user's chosen theme otherwise.
 *
 * The forced theme is applied with `persist = false`, so it never clobbers the
 * user's saved `cf:theme`. On unmount (or when `when` flips false) we restore
 * the store's active theme (the user's real choice), also without persisting.
 *
 * Apply/restore run in a layout effect so the `data-theme` toggle lands BEFORE
 * paint — on SPA nav into a forced surface this kills the one-frame
 * themed→Daylight flash that a passive `useEffect` would leave.
 *
 * Nested / overlapping forcing is unsupported: the model is apply-on-mount,
 * restore-store-theme-on-unmount, so two simultaneously mounted forcing
 * surfaces would restore to the store rather than to each other. In practice
 * only one forced surface (a marketing or auth page) is mounted at a time.
 */
export function useForcedTheme(id: ThemeId, options?: ForcedThemeOptions): void {
  const enabled = useFeatureFlag('theme_picker')
  const when = options?.when ?? true

  useLayoutEffect(() => {
    if (!enabled || !when) return
    // Record the force BEFORE applying so ThemeManager's flag-load reconcile
    // (a passive effect, runs after this layout effect) sees it and defers to
    // the forced theme instead of re-applying the user's stored theme.
    setActiveForcedTheme(id)
    applyTheme(id, false)
    return () => {
      // Stop forcing and restore the user's active theme (their real choice).
      setActiveForcedTheme(null)
      applyTheme(useThemeStore.getState().theme, false)
    }
  }, [enabled, when, id])
}
